import { useRef, useState } from 'react'
import { Button } from './Button'
import { Input } from './Input'

interface SignatureCaptureProps {
  label: 'signature' | 'initials'
  defaultText?: string
  // Optional per-tenant brand color (see SettingsPage.tsx) — themes the UI
  // chrome (tabs, active states, "Use This") only, never the actual
  // signature ink, which stays a neutral dark navy regardless of branding.
  accentColor?: string
  onSave: (dataUrl: string) => void
  onCancel: () => void
}

// Cursive/handwriting Google Fonts loaded in index.html — picking one and
// typing a name renders that text to a PNG below, so it flows through the
// exact same signedFields contract as a drawn stroke (see
// backend/src/pdf-sign.ts, which only ever embeds a PNG either way).
const FONT_OPTIONS = [
  { id: 'dancing-script', family: "'Dancing Script', cursive" },
  { id: 'great-vibes', family: "'Great Vibes', cursive" },
  { id: 'pacifico', family: "'Pacifico', cursive" },
  { id: 'sacramento', family: "'Sacramento', cursive" },
  { id: 'alex-brush', family: "'Alex Brush', cursive" },
]

// A full signature naturally sprawls wide (cursive, a whole name), but
// initials are just 1-2 letters — using the same wide 480x160 canvas for
// both meant initials ended up tiny in the middle of a lot of blank canvas,
// baked directly into the exported PNG (see SignatureFieldPlacer.tsx's
// STANDARD_SIZE_PCT.initial, sized to match this narrower aspect ratio).
const CANVAS_SIZE: Record<'signature' | 'initials', { width: number; height: number }> = {
  signature: { width: 480, height: 160 },
  initials: { width: 220, height: 160 },
}

// Combines free-hand drawing and a typed-name/fancy-font option into a
// single "capture your signature/initials" step — used once up front (see
// SignPage.tsx) rather than re-prompted at every field on the document.
export const SignatureCapture: React.FC<SignatureCaptureProps> = ({ label, defaultText, accentColor, onSave, onCancel }) => {
  const [mode, setMode] = useState<'draw' | 'type'>('draw')
  const canvasSize = CANVAS_SIZE[label]
  const accentBgStyle: React.CSSProperties | undefined = accentColor ? { backgroundColor: accentColor } : undefined
  const accentBorderTextStyle: React.CSSProperties | undefined = accentColor ? { borderColor: accentColor, color: accentColor } : undefined

  // ---------- Draw mode ----------
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawingRef = useRef(false)
  const [hasDrawn, setHasDrawn] = useState(false)

  // The canvas's on-screen (CSS) size and its drawing-buffer size aren't the
  // same — className="w-full" stretches it to fill the modal, and with no
  // CSS height override the browser derives the displayed height from the
  // buffer's own aspect ratio. Without correcting for that scale factor
  // here, a drawn point lands wherever the *unscaled* pixel offset happens
  // to fall on the (differently-sized) buffer, well away from the pointer.
  const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY }
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.setPointerCapture(e.pointerId)
    drawingRef.current = true
    const { x, y } = getPos(e)
    canvas.getContext('2d')!.beginPath()
    canvas.getContext('2d')!.moveTo(x, y)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!
    const { x, y } = getPos(e)
    ctx.strokeStyle = '#0F2742'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineTo(x, y)
    ctx.stroke()
    setHasDrawn(true)
  }

  const handlePointerUp = () => {
    drawingRef.current = false
  }

  const handleClear = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height)
    setHasDrawn(false)
  }

  const saveDrawn = () => onSave(canvasRef.current!.toDataURL('image/png'))

  // ---------- Type mode ----------
  const [typedText, setTypedText] = useState(defaultText || '')
  const [selectedFont, setSelectedFont] = useState(FONT_OPTIONS[0])
  const [renderingTyped, setRenderingTyped] = useState(false)

  const saveTyped = async () => {
    if (!typedText.trim()) return
    setRenderingTyped(true)
    try {
      // Force the web font to actually be loaded before drawing to canvas —
      // otherwise the first render can silently fall back to a system font.
      await document.fonts.load(`48px ${selectedFont.family}`, typedText)
      await document.fonts.ready

      const canvas = document.createElement('canvas')
      canvas.width = canvasSize.width
      canvas.height = canvasSize.height
      const ctx = canvas.getContext('2d')!
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.fillStyle = '#0F2742'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.font = `56px ${selectedFont.family}`
      ctx.fillText(typedText.trim(), canvas.width / 2, canvas.height / 2, canvas.width - 20)
      onSave(canvas.toDataURL('image/png'))
    } finally {
      setRenderingTyped(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-gray-200">
        {(['draw', 'type'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${mode === m ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            style={mode === m ? accentBorderTextStyle : undefined}
          >
            {m === 'draw' ? 'Draw' : 'Type'}
          </button>
        ))}
      </div>

      {mode === 'draw' ? (
        <div className="space-y-3">
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            className="w-full border border-gray-300 rounded-lg bg-white touch-none cursor-crosshair"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          />
          <div className="flex items-center justify-between gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={handleClear}>
              Clear
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="button" size="sm" style={accentBgStyle} disabled={!hasDrawn} onClick={saveDrawn}>
                Use This
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <Input
            label={`Type your ${label}`}
            value={typedText}
            onChange={(e) => setTypedText(e.target.value)}
            placeholder={label === 'initials' ? 'e.g. JS' : 'e.g. Jane Smith'}
          />

          <div className="grid grid-cols-1 gap-2">
            {FONT_OPTIONS.map((font) => (
              <button
                key={font.id}
                type="button"
                onClick={() => setSelectedFont(font)}
                className={`px-4 py-3 rounded-lg border text-2xl text-left transition-colors ${selectedFont.id === font.id ? 'border-primary ring-2 ring-accent/20 bg-primary/5' : 'border-gray-200 hover:bg-gray-50'}`}
                style={{ fontFamily: font.family, ...(selectedFont.id === font.id ? accentBorderTextStyle : undefined) }}
              >
                {typedText.trim() || (label === 'initials' ? 'JS' : 'Jane Smith')}
              </button>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" size="sm" style={accentBgStyle} disabled={!typedText.trim() || renderingTyped} onClick={saveTyped}>
              {renderingTyped ? 'Saving...' : 'Use This'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
